/**
 * VRG-716 — what a pasted product link POINTS AT, read from the URL alone (no fetch).
 *
 * Shared by the API's single-product import (apps/api demo/seed-products-from-url) and the
 * platform's de-duplication of the demo product list (components/input/ProductPicker), so the two
 * can never disagree about whether two links are the same product.
 *
 * Why this exists: pasting `ofyshop.com/products/crinkle-shirt-wispy-cloud?_pos=3&_sid=…&_ss=r` into
 * Create demo → Import re-added the store's first five catalogue products instead of that shirt.
 * The page has no schema.org Product markup (only an Organization node), so the importer fell
 * through to `/products.json` for the whole store — the same five the first step had proposed.
 * Reading the HANDLE out of the path lets the importer ask Shopify for exactly that product.
 */

/**
 * The WHATWG `URL`, typed by the subset used here. This package compiles against `lib: ES2020`
 * only (no DOM, no Node types), where `URL` is not declared, yet it is a runtime global in every
 * consumer (browsers, Node ≥ 10). Reached through `globalThis` so no ambient declaration leaks out.
 */
export interface WhatwgUrlLike {
  protocol: string;
  hostname: string;
  pathname: string;
  origin: string;
  hash: string;
  searchParams: {keys(): IterableIterator<string>; delete(name: string): void};
  toString(): string;
}
const UrlCtor = (globalThis as unknown as {URL: new (input: string) => WhatwgUrlLike}).URL;

export type ProductUrlKind =
  /** A single product: `/products/<handle>` (Shopify, incl. `/collections/<c>/products/<h>` and locale prefixes). */
  | 'product'
  /** A Shopify collection: `/collections/<handle>` with no product under it. A list, not a product. */
  | 'collection'
  /** Some other page with a path. Might be a product page on a non-Shopify site; only fetching tells. */
  | 'page'
  /** The site itself, no path. A request about the brand, never about one product. */
  | 'origin';

export interface ParsedProductUrl {
  /** The cleaned URL: https assumed, tracking parameters removed, hash dropped. */
  url: string;
  /** `https://ofyshop.com` (as typed: `www.` kept, because it is a different host to fetch). */
  origin: string;
  /** Lowercased host without `www.` — the identity used for de-duplication. */
  host: string;
  /** Path without a trailing slash (`''` for the origin). */
  path: string;
  kind: ProductUrlKind;
  /** Shopify product handle when `kind === 'product'`, else null. */
  handle: string | null;
}

/**
 * Query parameters that identify a CLICK, not a product. Shopify search/recommendation
 * (`_pos`, `_sid`, `_ss`, `_psq`, `_fid`, `_v`, `pr_*`), ad networks and newsletters. Matched
 * case-insensitively; `utm_*` and `pr_*` by prefix. `variant` is NOT here: it selects a real thing.
 */
const TRACKING_PARAMS = new Set([
  '_pos', '_sid', '_ss', '_psq', '_fid', '_v', '_ga', '_gl', '_kx', '_branch_match_id',
  'fbclid', 'gclid', 'gbraid', 'wbraid', 'dclid', 'msclkid', 'yclid', 'twclid', 'ttclid', 'li_fat_id',
  'mc_cid', 'mc_eid', 'igshid', 'srsltid', 'ref', 'ref_', 'referrer', 'si', 'spm',
  'oseid', 'klaviyo', 'sca_ref', 'sca_source', 'epik', 'hsa_acc', 'hsa_cam', 'hsa_grp', 'hsa_ad',
]);
const TRACKING_PREFIXES = ['utm_', 'pr_', 'hsa_', 'mtm_', 'pk_'];

const isTrackingParam = (name: string): boolean => {
  const n = name.toLowerCase();
  return TRACKING_PARAMS.has(n) || TRACKING_PREFIXES.some((p) => n.startsWith(p));
};

/** Remove tracking parameters and the fragment. Returns a NEW URL. */
export const stripTrackingParams = (input: WhatwgUrlLike): WhatwgUrlLike => {
  const url = new UrlCtor(input.toString());
  for (const name of [...url.searchParams.keys()]) {
    if (isTrackingParam(name)) url.searchParams.delete(name);
  }
  url.hash = '';
  return url;
};

/** `https://` is assumed when no scheme is typed; a host needs a dot (a bare word is not a site). */
const toUrl = (input: string): WhatwgUrlLike | null => {
  let s = (input ?? '').trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  try {
    const url = new UrlCtor(s);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.hostname.includes('.') ? url : null;
  } catch {
    return null;
  }
};

// Optional locale prefix (`/en`, `/en-us`, `/fr-ca`), optional collection, then `products/<handle>`.
// A trailing `.json` / `.js` / `.oembed` is tolerated so a pasted API link still names the product.
const SHOPIFY_PRODUCT_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2,4})?)?(?:\/collections\/[^/]+)?\/products\/([^/]+?)(?:\.(?:json|js|oembed))?$/i;
const SHOPIFY_COLLECTION_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2,4})?)?\/collections(?:\/[^/]+)?$/i;

const safeDecode = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

/** Read a pasted link. Null when it is not a usable http(s) URL. */
export const parseProductUrl = (input: string): ParsedProductUrl | null => {
  const raw = toUrl(input);
  if (!raw) return null;
  const url = stripTrackingParams(raw);
  const path = url.pathname.replace(/\/+$/, '');
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const base = {url: url.toString(), origin: url.origin, host, path};

  if (!path) return {...base, kind: 'origin', handle: null};
  const product = SHOPIFY_PRODUCT_PATH.exec(path);
  if (product?.[1]) return {...base, kind: 'product', handle: safeDecode(product[1]).toLowerCase()};
  if (SHOPIFY_COLLECTION_PATH.test(path)) return {...base, kind: 'collection', handle: null};
  return {...base, kind: 'page', handle: null};
};

/** Shopify's catalogue endpoint is where a whole list came from, never one product's page. */
const CATALOG_ENDPOINT = /\/products\.json$/i;

/**
 * The identities two product rows can share. Two rows are the same product when ANY key matches:
 *
 *   `url:<host><path>`      the listing, ignoring scheme, `www.`, query, hash and trailing slash
 *   `handle:<host>:<handle>` a Shopify handle on the same store (catches locale and collection paths)
 *   `sku:<sku>`             the SKU, case-insensitive
 *
 * `/products.json` is never a URL key: every row from one catalogue read shares it.
 */
export const productIdentityKeys = (row: {urls: readonly (string | null | undefined)[]; sku?: string | null}): string[] => {
  const keys = new Set<string>();
  for (const u of row.urls) {
    if (!u) continue;
    const parsed = parseProductUrl(u);
    if (!parsed || !parsed.path || CATALOG_ENDPOINT.test(parsed.path)) continue;
    keys.add(`url:${parsed.host}${parsed.path.toLowerCase()}`);
    if (parsed.handle) keys.add(`handle:${parsed.host}:${parsed.handle}`);
  }
  const sku = row.sku?.trim().toLowerCase();
  if (sku) keys.add(`sku:${sku}`);
  return [...keys];
};
